import Vue from 'vue';
import App from './App.vue';
import { router } from './router';
import './utils/common';
import { isDev } from './utils/common';
import * as mwx from '@/utils/common.js';

import { Lazyload } from 'vant';

Vue.use(Lazyload); // vant 懒加载指令 v-laz代替图片的src

// import * as fundebug from 'fundebug-javascript';
// import FundebugVue from 'fundebug-vue';

Vue.config.productionTip = false;
import { initSentryScope } from './utils/sentry';
import {
  initStabilityMonitoring,
  shouldCreateAutoHttpSpanForRequest
} from './utils/stability';
// 仅在显式开启性能打点且非生产环境时加载本地性能采集导出工具。
if (
  process.env.NODE_ENV !== 'production' &&
  process.env.VUE_APP_PERF_MONITOR === '1'
) {
  import('./utils/perfInject');
}
/**
 * 判断是否为无需上报的第三方或容器噪音错误。
 * @param {Object} event - Sentry 事件对象。
 * @returns {boolean} 是否应过滤该事件。
 */
function isThirdPartyNoiseEvent(event) {
  const exception =
    event && event.exception && event.exception.values
      ? event.exception.values[0]
      : null;
  const message = String((exception && exception.value) || event.message || '');
  const frames =
    exception && exception.stacktrace && exception.stacktrace.frames
      ? exception.stacktrace.frames
      : [];
  const hasFirstPartyFrame = frames.some((frame) => {
    const filename = String((frame && frame.filename) || '');
    return (
      filename.indexOf('/src/') > -1 || filename.indexOf('webpack://') > -1
    );
  });

  // 带一方业务栈的错误继续上报，避免误过滤真实业务异常。
  if (hasFirstPartyFrame) {
    return false;
  }

  return (
    message.indexOf('__TD_') > -1 ||
    message.indexOf('window.android.unLoad is not a function') > -1 ||
    message.indexOf(
      'nativeBridge.reportOperationGeneralEvent is not a function'
    ) > -1 ||
    message.indexOf('_dsbridge.call is not a function') > -1 ||
    message.indexOf('LIDNotify') > -1 ||
    message.indexOf('WeixinJSBridge') > -1 ||
    message.indexOf('jQuery') > -1 ||
    message.indexOf("Identifier 'Shop' has already been declared") > -1
  );
}

/**
 * 初始化生产环境 Sentry，开发环境跳过加载以减少本地 webpack 编译模块数。
 * @returns {Promise<void>} Sentry 初始化完成 Promise。
 */
function initProductionSentry() {
  // 开发环境不加载 Sentry，避免本地启动时编译 Sentry 依赖树。
  if (isDev) {
    return Promise.resolve();
  }

  return import('@sentry/vue')
    .then((Sentry) => {
      window._Sentry = Sentry;
      let openReplay = false;
      const sentryTracePropagationTargets = [
        /^https:\/\/xingbaidesign\.com/
        // /^http:\/\/app\.book\.com/
      ];
      try {
        const sentryBrowserTracingIntegration = Sentry.browserTracingIntegration(
          {
            router,
            tracePropagationTargets: sentryTracePropagationTargets,
            shouldCreateSpanForRequest: shouldCreateAutoHttpSpanForRequest
          }
        );
        Sentry.init({
          Vue,
          dsn:
            'https://0e2c8c0281dc3b9e03dcaffc0885e49c@o4509790235656192.ingest.us.sentry.io/4509796088610816',
          integrations: [
            sentryBrowserTracingIntegration
            // openReplay
            //   ? new Sentry.Replay({
            //       maskAllText: false,
            //       blockAllMedia: false
            //     })
            //   : null,
          ],
          tracePropagationTargets: sentryTracePropagationTargets,
          enableLogs: true,
          release: process.env.VUE_APP_SENTRY_RELEASE,
          environment: process.env.VUE_APP_SENTRY_ENVIRONMENT,
          // 2. 忽略常见无用报错
          ignoreErrors: [
            'object PromiseRejectionEvent',
            'Script error.',
            'AxiosError',
            'content risky',
            'Large Render Blocking Asset',
            'object ErrorEvent',
            'pollCheck fn',
            'object PromiseRejectionEvent',
            'Navigation cancelled',
            '获取图片信息失败',
            '订单不存在或已失效',
            '重试或联系商家客服',
            'fileToBase64 失败',
            '图片转换blobUrl失败',
            '图片转换base64失败',
            'FileReader encountered an error',
            // '图片上传失败',
            'uploadImage fail',
            'Network Error',
            'Network error 0',
            'getImageInfo repeat fail',
            '[object Event]',
            '超时',
            /^Loading chunk .* failed$/,
            /pollCheck timeout: \d+ms/,
            /^Loading CSS chunk .* failed\.?$/,
            // 正则匹配：过滤超时相关错误
            /超时,index=\d+,activeCanvasIndex=\d+/,
            // 过滤未知的 promise rejection 错误
            /Event `Event` \(type=error\) captured as promise rejection/,
            'heic2any error:name=FullSizeRender',
            '图片格式转换失败',
            '[object Object]',
            // 'timeout',
            'Object captured as promise rejection with keys: code, data, message, requestId',
            'undefined',
            'Large Render Blocking Asset',
            '当前图片超过抠图限制',
            'insertCSS is not defined',
            "Cannot read properties of undefined (reading 'checkDpi')"
          ],

          // 3. 过滤所有未处理的 Promise rejection，不上报 Sentry
          beforeSend(event) {
            // 第三方 SDK 或容器 bridge 噪音不进入 Sentry，业务栈错误继续上报。
            if (isThirdPartyNoiseEvent(event)) {
              return null;
            }
            const mech = event.exception?.values?.[0]?.mechanism;
            if (!mech) return event;

            // 过滤噪音：Promise未处理、fetch、xhr、console
            const noisyTypes = [
              'onunhandledrejection',
              'fetch',
              'xhr',
              'console'
            ];
            if (noisyTypes.includes(mech.type)) {
              return null;
            }
            // ✅ 关键：真正过滤 console.error（两种判断方式都写上，最稳）
            const isConsoleError =
              mech.type === 'console' ||
              (mech.handled === false && mech.synthetic === true);

            if (isConsoleError) {
              return null;
            }

            return event;
          },

          // 4. 去重 & 防抖
          maxBreadcrumbs: 50,
          // Performance Monitoring
          tracesSampleRate: 0.1, //  Capture 100% of the transactions
          // Session Replay
          replaysSessionSampleRate: openReplay ? 0.1 : 0, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
          replaysOnErrorSampleRate: openReplay ? 0.1 : 0 // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
        });
        initSentryScope();
        openReplay = true;
      } catch (error) {
        console.error(error);
      }
    })
    .catch((error) => {
      console.error(error);
    });
}
if (mwx.isWeChat()) {
  console.log('main初始化加载一次微信sdk>>>>');
  // 加载微信 JS SDK
  mwx.loadJsSdk('wx');
}
if (mwx.isWeChat()) {
  console.log('main初始化加载一次微信sdk>>>>');
  // 加载微信 JS SDK
  mwx.loadJsSdk('wx');
}
initStabilityMonitoring(router);

initProductionSentry().then(() => {
  new Vue({
    router,
    render: (h) => h(App)
  }).$mount('#app');
});
